import { type RefObject, useCallback, useMemo, useRef } from 'react';

import { FocusContext } from './FocusRestoreProvider.types';

const FocusRestoreProvider = ({
    children,
    shouldRestoreFocusOnReRegister = false,
}: React.PropsWithChildren<{ shouldRestoreFocusOnReRegister?: boolean }>) => {
    const refs = useRef(new Map<string, RefObject<HTMLElement | null>>());
    const lastFocusedKeyRef = useRef<string | null>(null);

    const register = useCallback(
        (key: string, ref: RefObject<HTMLElement | null>) => {
            refs.current.set(key, ref);

            if (
                shouldRestoreFocusOnReRegister &&
                key === lastFocusedKeyRef.current &&
                ref.current
            ) {
                ref.current.focus();
            }
        },
        [shouldRestoreFocusOnReRegister],
    );

    const unregister = useCallback((key: string) => {
        refs.current.delete(key);
    }, []);

    const setFocused = useCallback((key: string) => {
        if (refs.current.has(key)) {
            lastFocusedKeyRef.current = key;
        }
    }, []);

    const focusLast = useCallback(() => {
        if (lastFocusedKeyRef.current === null) return;
        const ref = refs.current.get(lastFocusedKeyRef.current);
        if (ref?.current) ref.current.focus();
    }, []);

    const memoized = useMemo(() => {
        return {
            register,
            unregister,
            setFocused,
            focusLast,
        };
    }, [register, unregister, setFocused, focusLast]);

    return <FocusContext.Provider value={memoized}>{children}</FocusContext.Provider>;
};

export default FocusRestoreProvider;
